/**
 * Router adapter — modern implementation (React Router 7, Vite brands).
 *
 * Exposes the same v5-shaped API as `./index.ts` on top of React Router 7, so that
 * core and brand code keep working unchanged while brands migrate one at a time.
 */
import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {
	Link as RouterLink,
	type Location,
	Navigate,
	type NavigateFunction,
	type To,
	useLocation,
	useNavigate,
	useNavigationType,
	useParams,
} from 'react-router';
import pathToRegexp from 'path-to-regexp';

import {isShellFlushed, reportLateHeader} from '../../ssrShell';
import {ServerContext} from '../sdk/modern';

type Action = 'POP' | 'PUSH' | 'REPLACE';
type LocationDescriptor = string | {pathname?: string; search?: string; hash?: string; state?: unknown};
type Listener = (location: Location, action: Action) => void;

export type Match = {
	path: string;
	url: string;
	isExact: boolean;
	params: Record<string, string>;
};

type MatchPathOptions = {
	path?: string | string[];
	exact?: boolean;
	strict?: boolean;
	sensitive?: boolean;
};

const listeners = new Set<Listener>();

const toRouterTarget = (to: LocationDescriptor): {to: To; state?: unknown} => {
	if (typeof to === 'string') {
		return {to};
	}

	const {state, ...path} = to || {};

	return {to: path, state};
};

const matchCache: Record<string, Record<string, {regexp: RegExp; keys: Array<{name: string | number}>}>> = {};

const compilePath = (path: string, options: {end: boolean; strict: boolean; sensitive: boolean}) => {
	const cacheKey = `${options.end}${options.strict}${options.sensitive}`;
	const pathCache = matchCache[cacheKey] || (matchCache[cacheKey] = {});

	if (!pathCache[path]) {
		const keys = [];

		pathCache[path] = {regexp: pathToRegexp(path, keys, options), keys};
	}

	return pathCache[path];
};

/**
 * React Router v5 `matchPath` (path-to-regexp v1 semantics), kept because brand route
 * tables rely on v5 patterns such as `/:locale/*` and `exact`.
 */
export const matchPath = (pathname: string, options: MatchPathOptions | string | string[] = {}): Match | null => {
	const {path, exact = false, strict = false, sensitive = false} =
		typeof options === 'string' || Array.isArray(options) ? {path: options} : options;

	return [].concat(path).reduce((matched: Match | null, path: string) => {
		if ((!path && path !== '') || matched) {
			return matched;
		}

		const {regexp, keys} = compilePath(path, {end: exact, strict, sensitive});
		const match = regexp.exec(pathname);

		if (!match) {
			return null;
		}

		const [url, ...values] = match;
		const isExact = pathname === url;

		if (exact && !isExact) {
			return null;
		}

		return {
			path,
			url: path === '/' && url === '' ? '/' : url,
			isExact,
			params: keys.reduce((memo, key, index) => {
				memo[key.name] = values[index];

				return memo;
			}, {}),
		};
	}, null);
};

const createHistory = (navigate: NavigateFunction, location: Location, action: Action) => {
	const navigateTo = (to: LocationDescriptor, state: unknown, replace: boolean) => {
		const target = toRouterTarget(to);

		// As history v4 (`createLocation`): the second argument only applies when the descriptor has no state.
		navigate(target.to, {replace, state: target.state !== undefined ? target.state : state});
	};

	return {
		action,
		location,
		get length() {
			return typeof window === 'undefined' ? 0 : window.history.length;
		},
		push: (to: LocationDescriptor, state?: unknown) => navigateTo(to, state, false),
		replace: (to: LocationDescriptor, state?: unknown) => navigateTo(to, state, true),
		go: (delta: number) => navigate(delta),
		goBack: () => navigate(-1),
		goForward: () => navigate(1),
		listen: (listener: Listener) => {
			listeners.add(listener);

			return () => {
				listeners.delete(listener);
			};
		},
	};
};

/**
 * v5-compatible `useHistory()`.
 */
export const useRouterHistory = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const action = useNavigationType() as Action;

	return useMemo(() => createHistory(navigate, location, action), [navigate, location, action]);
};

export const useRouterLocation = useLocation;

export {useParams};

/**
 * Notifies `history.listen` subscribers after every navigation. Rendered once in `root.tsx`.
 */
export const RouterListenerBridge = (): null => {
	const location = useLocation();
	const action = useNavigationType() as Action;
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}

		listeners.forEach((listener) => listener(location, action));
	}, [location]);

	return null;
};

type LinkProps = Omit<React.ComponentProps<typeof RouterLink>, 'to'> & {
	to: LocationDescriptor;
	innerRef?: React.Ref<HTMLAnchorElement>;
};

/**
 * v5-compatible `<Link>`: accepts `state` inside the `to` object and `innerRef`.
 */
export const Link = ({to, innerRef, ref, state, ...rest}: LinkProps) => {
	const target = toRouterTarget(to);

	return <RouterLink ref={innerRef || ref} to={target.to} state={state ?? target.state} {...rest} />;
};

type NavLinkProps = LinkProps & {
	activeClassName?: string;
	activeStyle?: React.CSSProperties;
	exact?: boolean;
	strict?: boolean;
	isActive?: (match: Match | null, location: Location) => boolean;
	className?: string;
	style?: React.CSSProperties;
};

const escapePath = (path: string) => path.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1');

/**
 * v5-compatible `<NavLink>` (`activeClassName`, `activeStyle`, `exact`, `strict`, `isActive`).
 */
export const NavLink = ({
	activeClassName = 'active',
	activeStyle,
	exact,
	strict,
	isActive: isActiveProp,
	className,
	style,
	...rest
}: NavLinkProps) => {
	const location = useLocation();
	const toPath = typeof rest.to === 'string' ? rest.to.split(/[?#]/)[0] : rest.to?.pathname;
	const match = toPath ? matchPath(location.pathname, {path: escapePath(toPath), exact, strict}) : null;
	const isActive = isActiveProp ? !!isActiveProp(match, location) : !!match;

	return (
		<Link
			{...rest}
			aria-current={isActive ? 'page' : undefined}
			className={[className, isActive && activeClassName].filter(Boolean).join(' ') || undefined}
			style={isActive ? {...style, ...activeStyle} : style}
		/>
	);
};

/**
 * v5-compatible `<Redirect>`. During SSR it flags a redirect on the response
 * (handled by `entry.server`), on the client it navigates.
 */
export const Redirect = ({to, push = false}: {to: LocationDescriptor; push?: boolean}) => {
	const ctx = useContext(ServerContext) as {res?: {locals?: Record<string, unknown>; headersSent?: boolean}} | null;
	const target = toRouterTarget(to);

	if (typeof window === 'undefined') {
		const res = ctx?.res;
		const locals = res?.locals;

		if (locals && !locals._ssrRedirect) {
			const {pathname = '', search = '', hash = ''} =
				typeof target.to === 'string' ? {pathname: target.to} : target.to;
			const destination = `${pathname}${search}${hash}`;

			if (isShellFlushed(res)) {
				reportLateHeader(res, `redirect 302 ${destination}`);
			} else {
				locals._ssrRedirect = {statusCode: 302, destination};
				(locals._abortSSRRender as (() => void) | undefined)?.();
			}
		}

		return null;
	}

	return <Navigate to={target.to} state={target.state} replace={!push} />;
};
